import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppContext } from '../../../app/provider/useAppContext';
import { useToast } from '../../../app/provider/useToast';
import { appConfig } from '../../../config/app-config';
import { CART_ACTIONS } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import { STORAGE_KEYS } from '../../../shared/utils/storage';
import { loginService } from '../../auth';
import { applyDiscount } from '../services/applyDiscount';
import { getCart } from '../services/getCart';
import { removeItem } from '../services/removeItem';
import { updateQuantity } from '../services/updateQuantity';
import { CartView, CartGroupView } from '../types/cartTypes';
import { resolveCartCurrency } from '../utils/cartFormatting';
import { emitCartUpdate, subscribeToCartUpdates } from '../utils/cartUtil';

interface UseCartPageProps {
  config: { locale: string };
}

export const useCartPage = (_props: UseCartPageProps) => {
  const { showSuccess, showError } = useToast();
  const [context] = useAppContext();
  const navigate = useNavigate();
  const params = useParams<{ eventKey?: string, storeKey?: string }>();

  const storeKey = params.storeKey || appConfig.defaults.storeKey;
  let eventKey = params.eventKey;
  if (!eventKey || eventKey === 'undefined' || eventKey === 'null') {
    eventKey = appConfig.defaults.eventKey;
  }
  const scopedKey = storeKey ? `cartId_${storeKey}` : 'cartId';

  const [cart, setCart] = React.useState<CartView | null>(null);
  const [fetched, setFetched] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const resolveCurrency = React.useCallback((cartOverride: CartView | null): string => {
    return resolveCartCurrency(cartOverride, context?.currency as string);
  }, [context?.currency]);

  const getCurrentCart = React.useCallback(async (force = false): Promise<CartView | null> => {
    if (fetched && !force) return cart;
    try {
      const freshCart = await getCart();
      if (freshCart) {
        setCart(freshCart as CartView);
        emitCartUpdate(freshCart);
      }
      setFetched(true);
      return freshCart as CartView | null;
    } catch (error) {
      console.error('[useCartPage] Failed to fetch cart:', error);
      setFetched(false);
      return null;
    }
  }, [fetched, cart]);

  React.useEffect(() => {
    const sessionCartId = sessionStorage.getItem(scopedKey);
    const localCartId = localStorage.getItem(scopedKey);
    if (!sessionCartId && localCartId) {
      sessionStorage.setItem(scopedKey, localCartId);
    }

    getCurrentCart();

    const unsubscribe = subscribeToCartUpdates((updatedCart: CartView | null) => {
      if (updatedCart === null) {
        setCart(null);
        return;
      }

      if (updatedCart) {
        if (!updatedCart.lineItems || updatedCart.lineItems.length === 0) {
          setCart(null);
          return;
        }

        const processCart = async () => {
          const processedCart = await getCart();
          if (processedCart) {
            setCart(processedCart as CartView);
          } else {
            setCart(null);
          }
        };
        processCart();
      }
    });

    return () => unsubscribe();
  }, [scopedKey, getCurrentCart]);

  React.useEffect(() => {
    if (cart?.id) {
      localStorage.setItem(scopedKey, cart.id);
    }
  }, [cart, scopedKey]);

  // Sync prices
  React.useEffect(() => {
    const cartId = cart?.id;
    const syncPrices = async () => {
      if (!cartId) return;

      try {
        const result = await applyDiscount(cartId);
        if (result.priceChanges && result.priceChanges.length > 0) {
          showSuccess('Basket updated - Prices have been updated to reflect current rates');
          const updatedCart = await getCart();
          if (updatedCart) {
            setCart(updatedCart as CartView);
          }
        }
      } catch (error) {
        console.warn('[useCartPage] Price sync failed (non-blocking):', error);
        // Silently fail — price sync is non-blocking
      }
    };

    syncPrices();
  }, [cart?.id, showSuccess]);

  // Welcome toast logic
  React.useEffect(() => {
    const justLoggedIn = sessionStorage.getItem(STORAGE_KEYS.JUST_LOGGED_IN);
    if (!justLoggedIn) return;

    if (context?.userInfo) {
      const userInfo = context.userInfo as Record<string, string>;
      const userName = userInfo.firstName || userInfo.name;
      const title = userName ? `Welcome ${userName}` : 'Welcome';
      sessionStorage.removeItem(STORAGE_KEYS.JUST_LOGGED_IN);
      showSuccess(title, 'You are now logged in.');
    } else {
      const timer = setTimeout(() => {
        if (sessionStorage.getItem(STORAGE_KEYS.JUST_LOGGED_IN)) {
          sessionStorage.removeItem(STORAGE_KEYS.JUST_LOGGED_IN);
          showSuccess('Welcome', 'You are now logged in.');
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [context?.userInfo, showSuccess]);

  const handleLogin = React.useCallback(async () => {
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, currentPath);
    const url = await loginService.getLoginUrl();
    window.location.href = url;
  }, []);

  const incrementQuantity = async (group: CartGroupView) => {
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      const currentQty = group.mainItem?.quantity ?? 1;
      await updateQuantity(group, currentQty + 1, cart);
    } finally {
      setIsUpdating(false);
    }
  };

  const decrementQuantity = async (group: CartGroupView) => {
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      const currentQty = group.mainItem?.quantity ?? 1;
      if (currentQty > 1) {
        await updateQuantity(group, currentQty - 1, cart);
      } else {
        await removeItem(group, cart);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteCart = async () => {
    const existingCart = await getCurrentCart();
    sessionStorage.removeItem(scopedKey);
    localStorage.removeItem(scopedKey);
    setCart(null);
    emitCartUpdate(null);
    if (existingCart) {
      try {
        await api.post(CART_ACTIONS, {
          action: 'deleteCart',
          cartId: existingCart.id,
          version: existingCart.version,
        });
      } catch (error) {
        console.warn('[useCartPage] deleteCart cleanup failed (non-blocking):', error);
        // Ignore — cart is already cleared locally
      }
    }
  };

  const checkout = async () => {
    if (!cart?.id) {
      showError('Cart not found. Please refresh the page.');
      return;
    }

    const pingToken = localStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN);
    const isLoggedIn = !!(context?.loggedIn || pingToken);

    if (!isLoggedIn) {
      const redirectPath = `/${storeKey}/checkout`;
      localStorage.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, redirectPath);
      navigate(`/${storeKey}/${eventKey}/account?returnUrl=${encodeURIComponent(redirectPath)}`);
      return;
    }

    setIsUpdating(true);
    try {
      const result = await applyDiscount(cart.id);
      if (result.priceChanges && result.priceChanges.length > 0) {
        showSuccess('Prices have been updated in your basket. Please review before proceeding.');
        const updatedCart = await getCart();
        if (updatedCart) {
          setCart(updatedCart as CartView);
        }
        setIsUpdating(false);
        return;
      }
      setIsUpdating(false);
      navigate(`/${storeKey}/checkout`);
    } catch (error) {
      console.error('[useCartPage] Checkout price verification failed:', error);
      showError('Unable to verify prices. Please try again.');
      setIsUpdating(false);
    }
  };

  return {
    cart,
    fetched,
    setIsUpdating,
    getCurrentCart,
    handleLogin,
    incrementQuantity,
    decrementQuantity,
    deleteCart,
    checkout,
    resolveCurrency,
    storeKey,
    eventKey,
    scopedKey,
    showSuccess,
    showError,
  };
};
